export type GetThirdPartyInvoiceHeadersRequest = {
  CustomerId: string;
  ThirdPartyVendorId?: string;
  StartDate: Date | string;
  EndDate: Date | string;
};

export interface IThirdPartyInvoiceDetailRequest {
  customerId: string;
}

export interface CreateThirdPartyInvoiceHeaderRequest {
  CustomerId: string;
  InvoiceNumber: string;
  InvoiceDate: Date | string;
  PONumber: string;
  Comment: string;
  TotalQuantity?: number;
  TotalDollars: number;
  TotalTax?: number;
  ThirdPartyVendorId: string;
}

export interface UpdateThirdPartyInvoiceHeaderRequest {
  ThirdPartyInvoiceHeaderId: string;
  InvoiceNumber: string;
  InvoiceDate: string;
  PONumber: string;
  Comment: string;
  TotalQuantity?: number;
  TotalDollars: number;
  TotalTax?: number;
}

export interface ThirdPartyInvoiceDetail {
  ThirdPartyInvoiceDetailId: string;
  LineType: number;
  ThirdPartyVendorProductId: string;
  ThirdPartyVendorId: string;
  ProductDescription: string;
  Brand: string;
  ManufacturererNumber: string;
  UPC: string;
  IsProductVisible: boolean;
  CreateDate: Date | string;
  ChangeDate: Date | string;
  ChangeUserName: string;
  ChangeCustomerId: string;
  IsOnList: boolean;
  ThirdPartyInvoiceDetailProductUnitOfMeasures: ThirdPartyInvoiceDetailProductUnitOfMeasure[];
}

export interface ThirdPartyInvoiceDetailProductUnitOfMeasure {
  ThirdPartyUnitOfMeasureType: number;
  ThirdPartyUnitOfMeasureTypeAbbreviation: string;
  ThirdPartyUnitOfMeasureTypeName: string;
  ProductNumber: string;
  QuantityOrdered?: number;
  QuantityShipped?: number;
  Weight?: number;
  PricePerCase?: number;
  ExtendedPrice?: number;
}

export const convertToISO8601 = (dateString: string): string => {
  if (dateString) {
    // Parse the input date string (mm/dd/yyyy)
    const [month, day, year] = dateString.split('/').map(Number);

    // Create a new Date object in local time
    const date = new Date(year, month - 1, day);

    // Adjust for timezone offset
    const timezoneOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
    const adjustedDate = new Date(date.getTime() - timezoneOffset);

    // Convert to ISO 8601 format (YYYY-MM-DDTHH:mm:ss)
    return adjustedDate.toISOString().split('.')[0];
  } else {
    return '';
  }
};

export function convertToMMDDYYYY(isoString: string, fullYear: boolean): string {
  // Parse the ISO 8601 date string into a Date object
  const date = new Date(isoString);

  // Extract the month, day, and year from the Date object
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const day = String(date.getDate()).padStart(2, '0');

  let year;

  if (fullYear) {
    year = String(date.getFullYear());
  } else {
    year = String(date.getFullYear()).slice(-2);
  }

  // Return the formatted date string
  return `${month}/${day}/${year}`;
}

export interface ThirdPartyInvoiceHeader {
  ThirdPartyInvoiceHeaderId: string;
  CustomerId: string;
  ThirdPartyVendor: ThirdPartyVendor;
  InvoiceNumber: string;
  InvoiceDate: string;
  PONumber: string;
  Comment: string;
  TotalQuantity?: number;
  TotalDollars: number;
  TotalTax?: number;
  CreateUserId: string;
  CreateUserDisplay: string;
  CreateDate: string;
  ChangeUserId: string;
  ChangeUserDisplay: string;
  ChangeDate: string;
  DeleteDate?: string;
}

export interface ThirdPartyVendor {
  ThirdPartyVendorId: string;
  VendorName: string;
  Street: string;
  StreetAdditional: string;
  City: string;
  State: string;
  Zip: string;
  Phone: string;
  Email: string;
  Comment: string;
  CreateDate: string;
  CreateUserId: string;
  ChangeUserId: string;
  ChangeCustomerId: string;
  ChangeDate: string;
  ChangeCustomerName: string;
  ChangeUserName: string;
  ProductCount: number;
}

export interface DeleteThirdPartyInvoiceHeaderRequest {
  ThirdPartyInvoiceHeaderId: string;
}

export interface UpdateThirdPartyInvoiceDetailsRequest {
  RequestItems: RequestItem[];
  ThirdPartyInvoiceHeaderId: string;
}

export interface RequestItem {
  ThirdPartyInvoiceDetailId: string;
  ThirdPartyVendorProductId: string;
  ProductDescription: string;
  LineType: number;
  UpdateThirdPartyInvoiceDetailUnitOfMeasureRequestItems: UpdateThirdPartyInvoiceDetailUnitOfMeasureRequestItem[];
}

export interface UpdateThirdPartyInvoiceDetailUnitOfMeasureRequestItem {
  ThirdPartyUnitOfMeasureType: number;
  ProductNumber: string;
  QuantityOrdered: number | null;
  QuantityShipped: number;
  Weight: number | null;
  PricePerCase: number;
  ExtendedPrice?: number;
}

export interface DeleteThirdPartyInvoiceDetailRequest {
  ThirdPartyInvoiceHeaderId: string;
  ThirdPartyInvoiceDetailId: string;
}

export interface QuickAddThirdPartyInvoiceDetailRequest {
  ThirdPartyInvoiceHeaderId: string;
  LineType: number;
  ProductNumber: string;
  QuantityOrdered?: number;
  QuantityShipped: number;
  Weight?: number;
  PricePerCase: number;
  ThirdPartyUnitOfMeasureType?: number;
  ThirdPartyVendorProductId?: string;
}

export enum InvoiceDetailsLineType {
  Invoice = 0,
  Credit = 1,
  Promotion = 3,
}
